import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../components/ui/Badge';
import {
  Callout,
  CountChip,
  MapPinIcon,
  PencilIcon,
  ShipIcon,
  TicketIcon,
  UserIcon,
} from '../../components/ui';
import { formatDate, formatDateTime } from '../../config/appConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { CrewBackButton } from './CrewBackButton';
import { CrewHelpHint } from './CrewReportSearchHeader';
import { TRAVEL_TICKETS_HELP } from './travelTicketsHelp';
import type {
  CrewTravelKind,
  CrewTravelPassenger,
  CrewTravelRow,
  CrewTravelSegment,
  CrewTravelSide,
  CrewTravelStatus,
  CrewTravelTravelerRole,
} from './types';

function kindLabel(kind: CrewTravelKind) {
  return kind === 'CREW_CHANGE' ? 'Changing crew' : 'Visit';
}

function kindBadge(kind: CrewTravelKind): 'info' | 'default' {
  return kind === 'CREW_CHANGE' ? 'info' : 'default';
}

function statusBadge(status: CrewTravelStatus): 'default' | 'warning' | 'success' | 'danger' | 'info' {
  switch (status) {
    case 'DRAFT':
      return 'default';
    case 'BOOKED':
      return 'info';
    case 'ISSUED':
      return 'success';
    case 'CANCELLED':
      return 'danger';
    default:
      return 'default';
  }
}

function roleLabel(role: CrewTravelTravelerRole) {
  const map: Record<CrewTravelTravelerRole, string> = {
    CREW: 'Crew',
    SUPERINTENDENT: 'Superintendent',
    AUDITOR: 'Auditor',
    TECHNICIAN: 'Technician',
    SPOUSE: 'Spouse',
    CHILD: 'Child',
    AGENT: 'Agent',
    OTHER: 'Other',
  };
  return map[role] ?? role;
}

function segmentKindLabel(kind: CrewTravelSegment['kind'], kindName?: string | null) {
  if (kindName?.trim()) return kindName.trim();
  const map: Record<string, string> = {
    FLIGHT: 'Flight',
    HOTEL: 'Hotel',
    TRANSFER: 'Transfer',
    OTHER: 'Other',
  };
  return map[kind] ?? kind;
}

function segmentWhen(segment: CrewTravelSegment): string {
  if (segment.startAt || segment.endAt) {
    return [segment.startAt ? formatDateTime(segment.startAt) : null, segment.endAt ? formatDateTime(segment.endAt) : null]
      .filter(Boolean)
      .join(' → ');
  }
  return segment.travelDate ? formatDate(segment.travelDate) : '—';
}

function segmentRoute(segment: CrewTravelSegment): string {
  return [segment.fromPlace?.trim(), segment.toPlace?.trim()].filter(Boolean).join(' → ') || '—';
}

function sortSegments(a: CrewTravelSegment, b: CrewTravelSegment): number {
  const ta = a.startAt || a.travelDate || '';
  const tb = b.startAt || b.travelDate || '';
  if (ta < tb) return -1;
  if (ta > tb) return 1;
  return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <div className="min-w-0">
      <div className={cn('text-[10px] font-semibold uppercase tracking-wide', colors.textMuted)}>{label}</div>
      <div className={cn('mt-0.5 text-sm break-words', colors.text)}>{children}</div>
    </div>
  );
}

function Panel({
  title,
  subtitle,
  icon,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { colors } = useTheme();
  return (
    <section className={cn('rounded-lg border shadow-sm overflow-hidden', colors.surface, colors.border)}>
      <div className={cn('flex items-center justify-between gap-2 border-b px-3 py-1.5', colors.border, colors.headerBg)}>
        <div className="min-w-0 flex items-center gap-2">
          {icon ? <span className={cn('shrink-0', colors.textMuted)}>{icon}</span> : null}
          <div className="min-w-0 flex items-baseline gap-2">
            <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
            {subtitle ? (
              <p className={cn('text-[11px] truncate hidden sm:block', colors.textMuted)}>{subtitle}</p>
            ) : null}
          </div>
        </div>
        {action}
      </div>
      <div className="p-3">{children}</div>
    </section>
  );
}

/** Numbered story of one person's legs */
function PersonJourney({
  title,
  badge,
  badgeVariant,
  name,
  role,
  summary,
  steps,
  tone,
}: {
  title: string;
  badge: string;
  badgeVariant: 'warning' | 'success' | 'info' | 'default';
  name: string;
  role: string;
  summary: string;
  steps: Array<{ kind: string; when: string; route: string; note?: string | null; cost?: string | null }>;
  tone: 'soff' | 'son' | 'visit';
}) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const shell =
    tone === 'son'
      ? isDark
        ? 'border-emerald-500/40 bg-emerald-500/5'
        : 'border-emerald-200 bg-emerald-50/70'
      : tone === 'soff'
        ? isDark
          ? 'border-amber-500/40 bg-amber-500/5'
          : 'border-amber-200 bg-amber-50/70'
        : cn(colors.border, colors.surface);

  return (
    <div className={cn('rounded-lg border px-3 py-3 space-y-3', shell)}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={badgeVariant} className="rounded-md px-1.5 py-0 text-[10px]">
              {badge}
            </Badge>
            <span className={cn('text-[10px] font-semibold uppercase tracking-wide', colors.textMuted)}>
              {title}
            </span>
          </div>
          <div className={cn('text-sm font-semibold', colors.text)}>{name}</div>
          <div className={cn('text-xs', colors.textMuted)}>{role}</div>
        </div>
      </div>
      <p className={cn('text-xs leading-relaxed', colors.text)}>{summary}</p>
      {steps.length ? (
        <ol className="space-y-2">
          {steps.map((step, i) => (
            <li key={`${step.kind}-${i}`} className="flex gap-2.5">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                  tone === 'son'
                    ? isDark
                      ? 'bg-emerald-500/30 text-emerald-100'
                      : 'bg-emerald-200 text-emerald-900'
                    : tone === 'soff'
                      ? isDark
                        ? 'bg-amber-500/30 text-amber-100'
                        : 'bg-amber-200 text-amber-900'
                      : isDark
                        ? 'bg-sky-500/30 text-sky-100'
                        : 'bg-sky-100 text-sky-900',
                )}
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="info" className="rounded-md px-1.5 py-0 text-[10px]">
                    {step.kind}
                  </Badge>
                  <span className={cn('text-xs font-medium', colors.text)}>{step.route}</span>
                </div>
                <div className={cn('text-[11px]', colors.textMuted)}>{step.when}</div>
                {step.note ? <div className={cn('text-[11px]', colors.textSecondary)}>{step.note}</div> : null}
                {step.cost ? <div className={cn('text-[11px] font-medium', colors.textMuted)}>{step.cost}</div> : null}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className={cn('text-xs', colors.textMuted)}>No travel legs yet for this person.</p>
      )}
    </div>
  );
}

function TravelerMeta({ passenger }: { passenger: CrewTravelPassenger }) {
  const { colors } = useTheme();
  return (
    <div className={cn('grid grid-cols-1 gap-2 text-xs sm:grid-cols-3', colors.textMuted)}>
      <span>Nationality: <span className={colors.text}>{passenger.nationality || '—'}</span></span>
      <span>Passport: <span className={colors.text}>{passenger.passport || '—'}</span></span>
      <span>
        DOB:{' '}
        <span className={colors.text}>
          {passenger.dateOfBirth ? formatDate(passenger.dateOfBirth) : '—'}
        </span>
      </span>
    </div>
  );
}

function segmentsForSide(
  segments: CrewTravelSegment[],
  side: CrewTravelSide,
  passengerId?: number | null,
): CrewTravelSegment[] {
  return segments
    .filter((s) => s.side === side || (passengerId != null && s.passengerId === passengerId))
    .sort(sortSegments);
}

function toSteps(segments: CrewTravelSegment[]) {
  return segments.map((s) => ({
    kind: segmentKindLabel(s.kind, s.kindName),
    when: segmentWhen(s),
    route: segmentRoute(s),
    note: s.notes,
    cost:
      s.cost != null
        ? `${Number(s.cost).toLocaleString(undefined, { maximumFractionDigits: 2 })}${s.currency ? ` ${s.currency}` : ''}`
        : null,
  }));
}

const TravelTicketDetailPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const canEdit = can('crew', 'edit');
  const { id: idParam } = useParams();
  const travelId = Number(idParam);

  const { data, error, isLoading } = useSWR<CrewTravelRow>(
    Number.isFinite(travelId) && travelId > 0 ? `/crew/travels/${travelId}` : null,
    fetcher,
    { revalidateOnFocus: false },
  );

  const dates = useMemo(() => {
    if (!data) return '—';
    return (
      [data.dateFrom ? formatDate(data.dateFrom) : null, data.dateTo ? formatDate(data.dateTo) : null]
        .filter(Boolean)
        .join(' → ') || '—'
    );
  }, [data]);

  const soff = useMemo(
    () => (data?.passengers ?? []).filter((p) => p.side === 'SOFF'),
    [data?.passengers],
  );
  const son = useMemo(
    () => (data?.passengers ?? []).filter((p) => p.side === 'SON'),
    [data?.passengers],
  );
  const visitPassengers = useMemo(
    () => (data?.passengers ?? []).filter((p) => p.side === 'VISIT' || data?.kind === 'VISIT'),
    [data?.passengers, data?.kind],
  );

  const familiarizationDays = useMemo(() => {
    if (!data || data.kind !== 'CREW_CHANGE') return null;
    const sonSegs = segmentsForSide(data.segments ?? [], 'SON', son[0]?.id);
    const soffSegs = segmentsForSide(data.segments ?? [], 'SOFF', soff[0]?.id);
    const sonJoin = sonSegs.find((s) => s.kind === 'TRANSFER')?.travelDate || sonSegs[0]?.travelDate;
    const soffLeave = soffSegs.find((s) => s.kind === 'TRANSFER')?.travelDate || soffSegs[0]?.travelDate;
    if (!sonJoin || !soffLeave) return null;
    const a = new Date(sonJoin);
    const b = new Date(soffLeave);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    const days = Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
    return days > 0 ? days : null;
  }, [data, son, soff]);

  if (isLoading) {
    return <div className={cn('p-4 text-sm', colors.textSecondary)}>Loading…</div>;
  }

  if (error || !data) {
    return (
      <div className="h-full flex flex-col min-h-0 gap-2">
        <section className={cn('shrink-0 rounded-lg border shadow-sm px-3 py-2', colors.surface, colors.border)}>
          <div className="flex items-center gap-2">
            <CrewBackButton fallbackPath="/crew/crew-changes-cost" />
            <h1 className={cn('text-sm font-semibold', colors.text)}>Crew Changes Cost</h1>
          </div>
        </section>
        <p className={cn('rounded-lg border px-3 py-4 text-sm', colors.error)}>
          Could not load this Crew Changes Cost record.
        </p>
      </div>
    );
  }

  const pageTitle = data.kind === 'VISIT' ? 'Visit cost' : 'Crew change cost';
  const soffPerson = soff[0];
  const sonPerson = son[0];
  const soffSteps = toSteps(segmentsForSide(data.segments ?? [], 'SOFF', soffPerson?.id));
  const sonSteps = toSteps(segmentsForSide(data.segments ?? [], 'SON', sonPerson?.id));

  return (
    <div className="h-full flex flex-col min-h-0 gap-2">
      <section className={cn('shrink-0 rounded-lg border shadow-sm px-3 py-2', colors.surface, colors.border)}>
        <div className="flex flex-wrap items-center gap-2">
          <CrewBackButton fallbackPath="/crew/crew-changes-cost" />
          <CrewHelpHint hint={TRAVEL_TICKETS_HELP} className="self-center" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <TicketIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} />
              <h1 className={cn('text-sm font-semibold tracking-tight', colors.text)}>{pageTitle}</h1>
              <Badge variant={kindBadge(data.kind)} className="rounded-md px-1.5 py-0 text-[10px]">
                {kindLabel(data.kind)}
              </Badge>
              <Badge variant={statusBadge(data.status)} className="rounded-md px-1.5 py-0 text-[10px]">
                {data.status}
              </Badge>
              {data.replacementId ? (
                <CountChip title="Linked replacement">#{data.replacementId}</CountChip>
              ) : null}
            </div>
            {data.passengerSummary ? (
              <p className={cn('mt-0.5 text-xs truncate', colors.textMuted)}>{data.passengerSummary}</p>
            ) : null}
          </div>
          {canEdit ? (
            <Link
              to={`/crew/crew-changes-cost/${data.id}/edit`}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-medium text-white shadow-sm',
                colors.primary,
              )}
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </Link>
          ) : null}
        </div>
      </section>

      <div className="flex-1 min-h-0 overflow-auto space-y-2 pb-2">
        {data.kind === 'CREW_CHANGE' ? (
          <Callout variant="info" title="How to read this record">
            <div className="space-y-1.5 text-xs leading-relaxed">
              <p>
                <strong>S/On (relief)</strong> arrives first and joins the vessel for familiarization
                {familiarizationDays != null ? ` (~${familiarizationDays} day${familiarizationDays === 1 ? '' : 's'})` : ''}.
              </p>
              <p>
                <strong>S/Off (outgoing)</strong> stays onboard during handover, then disembarks and flies home later —
                usually <em>not</em> on the same day as the relief.
              </p>
              <p className={colors.textMuted}>
                Read each person&apos;s journey top-to-bottom (numbered steps). Green = joining, amber = leaving.
              </p>
            </div>
          </Callout>
        ) : null}

        <Panel
          title="Travel Details"
          subtitle={data.kind === 'VISIT' ? 'Vessel, window, port' : 'Vessel, change window, port'}
          icon={<ShipIcon className="h-3.5 w-3.5" />}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Vessel">{data.vesselAbrv || data.vessel || '—'}</Field>
            <Field label="Ticket window">{dates}</Field>
            <Field label={data.kind === 'VISIT' ? 'Visit port' : 'Change port'}>
              {data.changePort || '—'}
            </Field>
            <Field label="Purpose">{data.purpose?.trim() || '—'}</Field>
          </div>
          {data.notes?.trim() ? (
            <div className="mt-3">
              <Field label="Notes">{data.notes}</Field>
            </div>
          ) : null}
        </Panel>

        {data.kind === 'CREW_CHANGE' ? (
          <Panel
            title="Who does what"
            subtitle="Separate journey for each seafarer"
            icon={<UserIcon className="h-3.5 w-3.5" />}
            action={<CountChip>{data.passengerCount}</CountChip>}
          >
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
              <div className="space-y-2">
                {sonPerson ? (
                  <>
                    <PersonJourney
                      title="Joining (relief)"
                      badge="S/On"
                      badgeVariant="success"
                      name={sonPerson.displayName || '—'}
                      role={roleLabel(sonPerson.role)}
                      summary="Flies to the change port early, rests (hotel), then transfers to the vessel and starts familiarization while the outgoing crew is still onboard."
                      steps={sonSteps}
                      tone="son"
                    />
                    <TravelerMeta passenger={sonPerson} />
                  </>
                ) : (
                  <p className={cn('text-xs rounded-lg border border-dashed px-3 py-4', colors.border, colors.textMuted)}>
                    No sign-on traveler yet — relief not assigned.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                {soffPerson ? (
                  <>
                    <PersonJourney
                      title="Leaving (outgoing)"
                      badge="S/Off"
                      badgeVariant="warning"
                      name={soffPerson.displayName || '—'}
                      role={roleLabel(soffPerson.role)}
                      summary="Remains onboard during handover / familiarization, then leaves the vessel and flies home — after the relief has already arrived."
                      steps={soffSteps}
                      tone="soff"
                    />
                    <TravelerMeta passenger={soffPerson} />
                  </>
                ) : (
                  <p className={cn('text-xs rounded-lg border border-dashed px-3 py-4', colors.border, colors.textMuted)}>
                    No sign-off traveler.
                  </p>
                )}
              </div>
            </div>
          </Panel>
        ) : (
          <Panel
            title="Travelers"
            subtitle="Visit passengers"
            icon={<UserIcon className="h-3.5 w-3.5" />}
            action={<CountChip>{data.passengerCount}</CountChip>}
          >
            {(visitPassengers.length ? visitPassengers : data.passengers).length ? (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {(visitPassengers.length ? visitPassengers : data.passengers).map((p, i) => {
                  const steps = toSteps(
                    (data.segments ?? [])
                      .filter((s) => s.side === 'VISIT' || s.passengerId === p.id)
                      .sort(sortSegments),
                  );
                  return (
                    <div key={p.id ?? `visit-${i}`} className="space-y-2">
                      <PersonJourney
                        title="Visitor"
                        badge="Visit"
                        badgeVariant="info"
                        name={p.displayName || '—'}
                        role={roleLabel(p.role)}
                        summary="Non-crew visit travel (inbound and return)."
                        steps={steps}
                        tone="visit"
                      />
                      <TravelerMeta passenger={p} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={cn('text-xs', colors.textMuted)}>No travelers</p>
            )}
          </Panel>
        )}

        {data.kind === 'CREW_CHANGE' && (data.segments?.length ?? 0) > 0 ? (
          <Panel
            title="Combined timeline"
            subtitle="Chronological — both people"
            icon={<MapPinIcon className="h-3.5 w-3.5" />}
          >
            <ol className="space-y-2">
              {[...(data.segments ?? [])].sort(sortSegments).map((s, i) => {
                const isSon = s.side === 'SON';
                const isSoff = s.side === 'SOFF';
                return (
                  <li
                    key={s.id ?? `all-${i}`}
                    className={cn(
                      'flex flex-wrap items-start gap-2 rounded-md border px-2.5 py-2 text-xs',
                      colors.border,
                      isSon
                        ? isDark
                          ? 'bg-emerald-500/10'
                          : 'bg-emerald-50'
                        : isSoff
                          ? isDark
                            ? 'bg-amber-500/10'
                            : 'bg-amber-50'
                          : null,
                    )}
                  >
                    <span className={cn('font-mono text-[10px] w-5 shrink-0', colors.textMuted)}>{i + 1}.</span>
                    <Badge
                      variant={isSon ? 'success' : isSoff ? 'warning' : 'default'}
                      className="rounded-md px-1.5 py-0 text-[10px]"
                    >
                      {isSon ? 'S/On' : isSoff ? 'S/Off' : '—'}
                    </Badge>
                    <Badge variant="info" className="rounded-md px-1.5 py-0 text-[10px]">
                      {segmentKindLabel(s.kind, s.kindName)}
                    </Badge>
                    <span className={cn('font-medium', colors.text)}>{segmentRoute(s)}</span>
                    <span className={colors.textMuted}>{segmentWhen(s)}</span>
                    {s.notes ? <span className={cn('basis-full pl-7', colors.textSecondary)}>{s.notes}</span> : null}
                  </li>
                );
              })}
            </ol>
          </Panel>
        ) : null}
      </div>
    </div>
  );
};

export default TravelTicketDetailPage;
